import React, {useEffect, useRef, useState} from 'react';
import root from 'window-or-global';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import {AccentColorOptions, useBreakpointUp} from 'epic-ue-theme';
import {GridContainer, VerticalSpacer} from '../../elements';
import {LicenseCard, LicenseCardProps} from './LicenseCard.molecule';
import {AnimateOnViewWrap} from '../../wraps/animation/AnimateOnViewWrap';

const LicensesWrapper = styled.div<{$topHeight: number; $midHeight: number; $bottomHeight: number}>`
    background-color: ${(p) => p.theme.palette.background.default};
    ${(p) => p.theme.breakpoints.up('md')} {
        height: auto;
        .license-card {
            height: ${(p) =>
                p.$topHeight && p.$midHeight && p.$bottomHeight
                    ? `${p.$topHeight + p.$midHeight + p.$bottomHeight}px`
                    : 'auto'};
        }
        .license-card-top {
            height: ${(p) => (p.$topHeight ? `${p.$topHeight}px` : 'auto')};
        }
        .active .license-card-top {
            height: ${(p) => (p.$topHeight ? `${p.$topHeight - 8}px` : 'auto')};
        }
        .license-card-mid {
            height: ${(p) => (p.$topHeight ? `${p.$midHeight}px` : 'auto')};
            top: ${(p) => (p.$topHeight ? `${p.$topHeight}px` : 'initial')};
        }
        .license-card-bottom {
            height: ${(p) => (p.$bottomHeight ? `${p.$bottomHeight}px` : 'auto')};
            top: ${(p) =>
                p.$topHeight && p.$midHeight ? `${p.$topHeight + p.$midHeight}px` : 'initial'};
        }
    }
`;

export interface LicensesProps {
    className?: string;
    accentColor?: AccentColorOptions;
    cards?: LicenseCardProps[];
    hidden?: boolean;
}

export const Licenses = (props: LicensesProps): JSX.Element | null => {
    const {className, cards = [], accentColor, hidden} = props;

    const isMdUp = useBreakpointUp('md');
    const shouldUpdateOnMd = useRef(true);
    const shouldDisableAnim = useRef(false);
    const [afterRender, setAfterRender] = useState(false);
    const [topHeight, setTopHeight] = useState(0);
    const [midHeight, setMidHeight] = useState(0);
    const [bottomHeight, setBottomHeight] = useState(0);

    const debounce = (fn, ms) => {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                timer = null;
                fn.apply(this, args);
            }, ms);
        };
    };
    const updateCardHeights = () => {
        setTopHeight(0);
        setMidHeight(0);
        setBottomHeight(0);
        const cardTops = root.document.getElementsByClassName('license-card-top');
        const cardMids = root.document.getElementsByClassName('license-card-mid');
        const cardBottoms = root.document.getElementsByClassName('license-card-bottom');
        const topHeights: number[] = [];
        const midHeights: number[] = [];
        const bottomHeights: number[] = [];
        for (let i = 0; i < cardTops.length; i++) {
            topHeights?.push(cardTops[i]?.getBoundingClientRect()?.height);
            midHeights?.push(cardMids[i]?.getBoundingClientRect()?.height);
            bottomHeights?.push(cardBottoms[i]?.getBoundingClientRect()?.height);
        }
        setTopHeight(Math.max(...topHeights));
        setMidHeight(Math.max(...midHeights));
        setBottomHeight(Math.max(...bottomHeights));
    };
    // handle resizes 10 times a second rather than every render
    const debouncedHandleResize = debounce(() => {
        if (!isMdUp) return null;
        updateCardHeights();
    }, 100);

    useEffect(() => {
        // if not isMdUp, prevent event listeners, but prepare refs for switch to 960px
        if (!isMdUp) {
            shouldUpdateOnMd.current = true;
            if (!afterRender) {
                shouldDisableAnim.current = true;
            }
            return;
        }
        // prevent event listeners until animations finish
        if (!afterRender) {
            setTimeout(() => {
                setAfterRender(true);
                debouncedHandleResize();
            }, 2000);
            return;
        }
        // manually force immediate updateCardHeights at 960px just once, then rely on listeners
        if (shouldUpdateOnMd.current) {
            setTimeout(() => {
                shouldUpdateOnMd.current = false;
                debouncedHandleResize();
            }, 1);
        }
        root.addEventListener('resize', debouncedHandleResize);
        return () => {
            root.removeEventListener('resize', debouncedHandleResize);
        };
    });

    if (hidden) return null;

    return (
        <LicensesWrapper
            className={className}
            $topHeight={topHeight}
            $midHeight={midHeight}
            $bottomHeight={bottomHeight}>
            <GridContainer>
                <VerticalSpacer variant="custom" spacing={5} />
                <Grid container direction="row" alignItems="stretch">
                    {cards &&
                        cards.length &&
                        cards.map((card, index) => (
                            <Grid item xs={12} md={4} key={`${card.title}-${index}-outer`}>
                                <AnimateOnViewWrap
                                    translateY={isMdUp ? 56 / (index + 1) : 56}
                                    delay={isMdUp ? 1 + index * 0.25 : 0}
                                    disable={isMdUp && shouldDisableAnim.current}>
                                    <LicenseCard
                                        key={`${card.title}-${index}-inner`}
                                        accentColor={accentColor}
                                        index={index}
                                        active={card.active}
                                        title={card.title}
                                        description={card.description}
                                        price={card.price}
                                        priceNote={card.priceNote}
                                        buttonText={card.buttonText}
                                        buttonHref={card.buttonHref}
                                        bulletsEyebrow={card.bulletsEyebrow}
                                        bullets={card.bullets}
                                    />
                                </AnimateOnViewWrap>
                            </Grid>
                        ))}
                </Grid>
            </GridContainer>
        </LicensesWrapper>
    );
};
